import { useState } from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight } from "lucide-react";
import INSIGHTS from "@/data/insightsData";
import DetailsModal from "@/components/landing/DetailsModal";

const FAQS = [
  {
    q: "What kind of brands does Quantum Star Marketing work with?",
    a: "We partner with ambitious DTC, SaaS, fintech, and enterprise brands generating AED 700K–AED 200M+ in annual revenue. Our engagements typically range from 6–24 months.",
  },
  {
    q: "How is Quantum Star different from a traditional agency?",
    a: "We replace 4–6 specialist agencies with a single elite pod aligned to your revenue. Our AI operating layer optimizes spend, creative and funnels continuously — not quarterly.",
  },
  {
    q: "What does a typical engagement look like?",
    a: "Week 1–2: Forensic audit + strategy. Week 3+: Execution across the chosen growth levers, with weekly performance reviews and a real-time board-grade dashboard.",
  },
  {
    q: "Do you guarantee results?",
    a: "We commit to clearly defined KPIs (ROAS, CAC, pipeline, MRR) in writing. If we miss benchmarks two consecutive quarters, you can exit penalty-free.",
  },
  {
    q: "How quickly can we start?",
    a: "Discovery typically begins within 5–7 business days of contract signing. Execution kicks off in week 2 once the strategy is approved.",
  },
];

export default function FaqInsights() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="insights"
      data-testid="faq-insights-section"
      className="relative py-14 md:py-32 overflow-hidden border-t border-white/5"
    >
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16">
        {/* FAQ */}
        <div>
          <div className="text-[10px] font-mono-acc uppercase tracking-[0.3em] text-cyan-400 mb-3">
            / FAQ
          </div>
          <h2 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            Questions, <span className="text-gradient-neon">answered.</span>
          </h2>

          <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="rounded-2xl glass border border-white/[0.06] px-5 data-[state=open]:bg-white/[0.04] data-[state=open]:border-purple-500/30 transition-colors"
              >
                <AccordionTrigger className="font-clash text-base md:text-lg font-medium text-white hover:no-underline py-5 text-left">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-400 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Insights / blog teaser */}
        <div>
          <div className="text-[10px] font-mono-acc uppercase tracking-[0.3em] text-cyan-400 mb-3">
            / Latest Insights
          </div>
          <h2 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
            From the <span className="text-gradient-neon">Quantum desk.</span>
          </h2>

          <div className="mt-10 flex flex-col">
            {INSIGHTS.map((p, i) => (
              <motion.button
                type="button"
                key={p.key}
                onClick={() => setActive(p)}
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="group flex items-center justify-between gap-6 border-b border-white/5 py-5 hover:border-purple-500/30 transition-colors text-left"
                data-testid={`insight-post-${i}`}
              >
                <div>
                  <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400/90 mb-2">
                    {p.tag}
                  </div>
                  <div className="font-clash text-lg md:text-xl font-medium text-white max-w-md leading-snug group-hover:text-cyan-200 transition-colors">
                    {p.name}
                  </div>
                  <div className="mt-1 text-xs text-zinc-500 max-w-md">{p.tagline}</div>
                </div>
                <span className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-zinc-400 group-hover:text-white group-hover:border-purple-400/50 group-hover:bg-white/5 transition-all">
                  <ArrowRight size={16} />
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {active && (
        <DetailsModal
          item={active}
          onClose={() => setActive(null)}
          testIdPrefix="insight-details"
        />
      )}
    </section>
  );
}
